import React, { useEffect, useState } from "react";
import axios from "axios";
import { TopNav } from "@/components/TopNav";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Calendar, Plus, MapPin, ChevronRight, X, LogIn, Maximize2, ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { pageTr } from "@/i18n/pageTranslations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = ["discovery", "scientific", "newstones", "market", "show", "focus"];

const JournalPage = () => {
  const { lang } = useLang();
  const t = pageTr[lang].journal;

  const [articles, setArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminPwd, setAdminPwd] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [tab, setTab] = useState("article"); // article | event
  const [zoomSrc, setZoomSrc] = useState(null);

  const loadAll = async () => {
    const [a, e] = await Promise.all([
      axios.get(`${API}/journal?lang=${lang}`),
      axios.get(`${API}/events?lang=${lang}`),
    ]);
    setArticles(a.data.articles || []);
    setEvents(e.data.events || []);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [lang]);

  const login = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/verify`, { password: adminPwd });
      setIsAdmin(!!data.ok);
    } catch { setIsAdmin(false); }
  };

  return (
    <div className="min-h-screen bg-[#050811] text-[#F8F5EE] flex flex-col">
      <SEO
        title={t.title}
        path="/journal"
        noindex
        description={lang === "fr"
          ? "GEM PRICE — Le journal mensuel de gemmologie : découvertes de gisements, recherches, analyses et tendances du marché des pierres précieuses."
          : "GEM PRICE — Monthly gemmology journal: mine discoveries, research, analysis and precious gemstone market trends."}
      />
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 md:px-14 py-10 space-y-8">
        <header className="flex items-center justify-between flex-wrap gap-3">
          <div className="hidden md:flex items-center gap-3 border border-[#C1A661]/30 rounded-full px-4 py-2">
            <Calendar size={16} className="text-[#C1A661]" />
            <span className="text-[10px] tracking-[0.25em] uppercase text-[#E2CFA4]">{t.newMonthly}</span>
          </div>
          <div className="text-center flex-1 order-first md:order-none w-full md:w-auto">
            <h1 className="font-serif text-3xl md:text-5xl tracking-[0.15em] text-[#E2CFA4]">GEM PRICE</h1>
            <p className="text-[13px] text-[#F8F5EE] mt-1">{t.title}</p>
            <p className="text-[11px] text-[#A1A7B3] mt-1 italic">{t.subtitle}</p>
          </div>
          <button
            data-testid="journal-admin-open"
            onClick={() => setAdminOpen(true)}
            className="text-[10px] tracking-[0.25em] uppercase text-[#C1A661] hover:text-[#E2CFA4] border border-[#C1A661]/30 hover:border-[#C1A661] rounded-full px-4 py-2 flex items-center gap-2"
            aria-label={t.adminOpen}
          >
            <LogIn size={13} /> <span className="hidden sm:inline">{t.adminOpen}</span>
          </button>
        </header>

        <div className="border border-[#C1A661]/20 rounded-sm p-6 bg-[#080C17]">
          <p className="text-lg text-[#F8F5EE] font-light mb-2">{t.intro}</p>
          <p className="text-[13px] text-[#A1A7B3] font-light">{t.body}</p>
        </div>

        <div className="text-center text-[11px] tracking-[0.3em] uppercase text-[#C1A661]">{t.summary}</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {CATS.map((c) => (
            <div key={c} data-testid={`cat-${c}`} className="border border-[#C1A661]/15 rounded-sm p-3 bg-[#080C17] text-center">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#E2CFA4] leading-tight">{t.categories[c]}</div>
            </div>
          ))}
        </div>

        {/* Articles */}
        <section data-testid="journal-articles">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl text-[#E2CFA4]">{t.articlesTitle}</h2>
            {isAdmin && <button data-testid="btn-add-article" onClick={() => { setTab("article"); setAdminOpen(true); }} className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#C1A661] border border-[#C1A661]/40 px-3 py-1.5 rounded-full"><Plus size={13} />{t.addArticle}</button>}
          </div>
          {articles.length === 0 ? (
            <p className="text-[13px] text-[#A1A7B3] italic">{t.empty}</p>
          ) : (
            <div className="space-y-3">
              {articles.map((a) => (
                <article key={a.id} className="border border-[#C1A661]/15 rounded-sm p-4 bg-[#080C17]">
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#C1A661] mb-1">{t.categories[a.category] || a.category}</div>
                      <h3 className="font-serif text-lg text-[#E2CFA4]">{a.title}</h3>
                      {a.excerpt && <p className="text-[12px] text-[#A1A7B3] font-light mt-1">{a.excerpt}</p>}
                      {a.body && <p className="text-[12px] text-[#D8DBE2] font-light mt-2 whitespace-pre-line">{a.body}</p>}
                      {a.author && <div className="text-[10px] text-[#8A8F99] mt-2 italic">— {a.author}</div>}
                      {a.image_url && a.image_url.startsWith("/journal/") && (
                        <button
                          type="button"
                          data-testid={`journal-open-fiche-${a.id}`}
                          onClick={() => setZoomSrc(a.image_url)}
                          className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C1A661] hover:text-[#E2CFA4] border border-[#C1A661]/40 hover:border-[#C1A661] rounded-full px-4 py-2 transition-colors"
                        >
                          <Maximize2 size={12} strokeWidth={1.5} />
                          <span>{lang === "fr" ? "Voir la fiche complète" : "View full sheet"}</span>
                        </button>
                      )}
                    </div>
                    {a.image_url && a.image_url.startsWith("/journal/") && (
                      <button
                        type="button"
                        onClick={() => setZoomSrc(a.image_url)}
                        className="w-20 md:w-32 shrink-0 rounded-sm border border-[#C1A661]/20 overflow-hidden hover:border-[#C1A661]/60 transition self-start"
                        aria-label={lang === "fr" ? "Voir la fiche complète" : "View full sheet"}
                      >
                        <img
                          src={a.image_url}
                          alt={a.title}
                          className="w-full h-auto object-cover cursor-zoom-in"
                          loading="lazy"
                        />
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Events */}
        <section data-testid="journal-events">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl text-[#E2CFA4]">{t.eventsTitle}</h2>
            {isAdmin && <button data-testid="btn-add-event" onClick={() => { setTab("event"); setAdminOpen(true); }} className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#C1A661] border border-[#C1A661]/40 px-3 py-1.5 rounded-full"><Plus size={13} />{t.addEvent}</button>}
          </div>
          {events.length === 0 ? (
            <p className="text-[13px] text-[#A1A7B3] italic">{t.empty}</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {events.map((e) => (
                <div key={e.id} className="border border-[#C1A661]/15 rounded-sm p-4 bg-[#080C17]">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#C1A661] mb-1">
                    <Calendar size={12} />
                    {new Date(e.start_date).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "long", year: "numeric" })}
                    {e.end_date && ` → ${new Date(e.end_date).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "long" })}`}
                  </div>
                  <div className="font-serif text-lg text-[#E2CFA4]">{e.name}</div>
                  <div className="flex items-center gap-2 text-[11px] text-[#A1A7B3] mt-1"><MapPin size={11} className="text-[#C1A661]" />{e.city}, {e.country}</div>
                  {e.url && <a href={e.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#C1A661] hover:text-[#E2CFA4]">{e.url}<ChevronRight size={12} /></a>}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {adminOpen && (
        <AdminPanel
          onClose={() => setAdminOpen(false)} t={t} lang={lang}
          isAdmin={isAdmin} adminPwd={adminPwd} setAdminPwd={setAdminPwd}
          login={login} tab={tab} setTab={setTab} onSaved={loadAll}
        />
      )}

      {zoomSrc && (
        <div
          data-testid="journal-zoom-overlay"
          className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
          onClick={() => setZoomSrc(null)}
        >
          <div className="flex items-center justify-between p-4 border-b border-[#C1A661]/20 bg-[#050B1E]/95">
            <button
              data-testid="journal-zoom-back-btn"
              onClick={(e) => { e.stopPropagation(); setZoomSrc(null); }}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] text-[#E2CFA4] hover:text-[#F8F5EE] text-xs tracking-[0.25em] uppercase transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              <span>{lang === "fr" ? "Retour" : "Back"}</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setZoomSrc(null); }}
              className="text-[#A1A7B3] hover:text-[#F8F5EE] transition-colors"
              aria-label="Close"
              data-testid="journal-zoom-close-btn"
            >
              <X size={22} strokeWidth={1.4} />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4 md:p-8 flex items-start justify-center" onClick={(e) => e.stopPropagation()}>
            <img src={zoomSrc} alt="Journal fiche" className="max-w-full h-auto rounded-sm shadow-2xl" style={{ backgroundColor: "#F8F5EE" }} />
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

const LockedBlock = () => null;

const AdminPanel = ({ onClose, t, lang, isAdmin, adminPwd, setAdminPwd, login, tab, setTab, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", category: "discovery", excerpt: "", body: "", image_url: "", author: "" });
  const [ev, setEv] = useState({ name: "", city: "", country: "", start_date: "", end_date: "", url: "" });
  const [grant, setGrant] = useState({ email: "", msg: "", err: "" });

  const submitArticle = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/journal`, { ...form, lang }, { headers: { "x-admin-password": adminPwd } });
      setForm({ title: "", category: "discovery", excerpt: "", body: "", image_url: "", author: "" });
      await onSaved();
      onClose();
    } finally { setSaving(false); }
  };
  const submitEvent = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/events`, { ...ev, lang }, { headers: { "x-admin-password": adminPwd } });
      setEv({ name: "", city: "", country: "", start_date: "", end_date: "", url: "" });
      await onSaved();
      onClose();
    } finally { setSaving(false); }
  };
  const submitGrant = async () => {
    setSaving(true);
    setGrant((g) => ({ ...g, msg: "", err: "" }));
    try {
      const { data } = await axios.post(`${API}/admin/grant-subscription`,
        { email: grant.email.trim() },
        { headers: { "x-admin-password": adminPwd } });
      setGrant({ email: "", msg: (lang === "fr" ? "Abonnement accordé à " : "Subscription granted to ") + data.email, err: "" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setGrant((g) => ({ ...g, msg: "", err: typeof detail === "string" ? detail : (lang === "fr" ? "Erreur inconnue" : "Unknown error") }));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-panel" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="relative bg-[#050B1E] border border-[#C1A661]/30 rounded-sm p-8 w-full max-w-2xl my-auto">
        <button data-testid="admin-close" onClick={onClose} className="absolute top-4 right-4 text-[#A1A7B3] hover:text-[#F8F5EE]"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#E2CFA4] mb-6">{t.adminTitle}</h3>

        {!isAdmin ? (
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C1A661] mb-2">{t.adminPwd}</label>
            <input
              data-testid="admin-pwd-input"
              type="password" value={adminPwd} onChange={(e) => setAdminPwd(e.target.value)}
              className="w-full bg-[#080C17] border border-[#C1A661]/25 rounded-sm px-4 py-2 text-[#F8F5EE] text-sm focus:border-[#C1A661] outline-none"
            />
            <button
              data-testid="admin-login-btn"
              onClick={login}
              className="mt-4 w-full py-2.5 rounded-sm bg-[#C1A661] hover:bg-[#E2CFA4] text-[#050B1E] text-[11px] tracking-[0.25em] uppercase font-semibold"
            >
              {t.adminLogin}
            </button>
          </div>
        ) : (
          <div>
            <div className="flex gap-2 border-b border-[#C1A661]/20 mb-4">
              <button data-testid="tab-article" onClick={() => setTab("article")} className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase ${tab === "article" ? "text-[#C1A661] border-b-2 border-[#C1A661]" : "text-[#A1A7B3]"}`}>{t.addArticle}</button>
              <button data-testid="tab-event" onClick={() => setTab("event")} className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase ${tab === "event" ? "text-[#C1A661] border-b-2 border-[#C1A661]" : "text-[#A1A7B3]"}`}>{t.addEvent}</button>
              <button data-testid="tab-grant" onClick={() => setTab("grant")} className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase ${tab === "grant" ? "text-[#C1A661] border-b-2 border-[#C1A661]" : "text-[#A1A7B3]"}`}>{lang === "fr" ? "Offrir abo" : "Grant sub"}</button>
            </div>
            {tab === "article" ? (
              <div className="space-y-3">
                <Field label={t.formArticle.title} testid="art-title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C1A661] mb-1">{t.formArticle.category}</label>
                  <select data-testid="art-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-[#080C17] border border-[#C1A661]/25 rounded-sm px-4 py-2 text-[#F8F5EE] text-sm">
                    {CATS.map((c) => <option key={c} value={c}>{t.categories[c]}</option>)}
                  </select>
                </div>
                <Field label={t.formArticle.excerpt} testid="art-excerpt" value={form.excerpt} onChange={(v) => setForm({ ...form, excerpt: v })} />
                <Field label={t.formArticle.body} testid="art-body" value={form.body} onChange={(v) => setForm({ ...form, body: v })} textarea />
                <Field label={t.formArticle.author} testid="art-author" value={form.author} onChange={(v) => setForm({ ...form, author: v })} />
                <button data-testid="art-submit" disabled={saving || !form.title} onClick={submitArticle}
                  className="mt-2 w-full py-2.5 rounded-sm bg-[#C1A661] hover:bg-[#E2CFA4] disabled:opacity-60 text-[#050B1E] text-[11px] tracking-[0.25em] uppercase font-semibold">
                  {t.formArticle.submit}
                </button>
              </div>
            ) : tab === "event" ? (
              <div className="space-y-3">
                <Field label={t.formEvent.name} testid="ev-name" value={ev.name} onChange={(v) => setEv({ ...ev, name: v })} />
                <Field label={t.formEvent.city} testid="ev-city" value={ev.city} onChange={(v) => setEv({ ...ev, city: v })} />
                <Field label={t.formEvent.country} testid="ev-country" value={ev.country} onChange={(v) => setEv({ ...ev, country: v })} />
                <Field label={t.formEvent.start} testid="ev-start" value={ev.start_date} onChange={(v) => setEv({ ...ev, start_date: v })} type="date" />
                <Field label={t.formEvent.end} testid="ev-end" value={ev.end_date} onChange={(v) => setEv({ ...ev, end_date: v })} type="date" />
                <Field label={t.formEvent.url} testid="ev-url" value={ev.url} onChange={(v) => setEv({ ...ev, url: v })} />
                <button data-testid="ev-submit" disabled={saving || !ev.name || !ev.start_date} onClick={submitEvent}
                  className="mt-2 w-full py-2.5 rounded-sm bg-[#C1A661] hover:bg-[#E2CFA4] disabled:opacity-60 text-[#050B1E] text-[11px] tracking-[0.25em] uppercase font-semibold">
                  {t.formEvent.submit}
                </button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="grant-panel">
                <div className="text-[11px] text-[#A1A7B3] leading-relaxed">
                  {lang === "fr"
                    ? "Offrez un abonnement Premium gratuit à un compte existant (VIP, presse, gagnant, compensation). L'utilisateur doit d'abord s'être inscrit."
                    : "Grant a free Premium subscription to an existing account (VIP, press, contest winner, compensation). The user must already be signed up."}
                </div>
                <Field label={lang === "fr" ? "Email du compte" : "Account email"} testid="grant-email"
                  value={grant.email}
                  onChange={(v) => setGrant((g) => ({ ...g, email: v, msg: "", err: "" }))} />
                {grant.msg && (
                  <div data-testid="grant-msg" className="text-[11px] text-[#C1A661] bg-[#C1A661]/10 border border-[#C1A661]/25 rounded-sm px-3 py-2">
                    {grant.msg}
                  </div>
                )}
                {grant.err && (
                  <div data-testid="grant-err" className="text-[11px] text-[#F4A3A3] bg-[#F4A3A3]/10 border border-[#F4A3A3]/25 rounded-sm px-3 py-2">
                    {grant.err}
                  </div>
                )}
                <button data-testid="grant-submit" disabled={saving || !grant.email.includes("@")} onClick={submitGrant}
                  className="mt-2 w-full py-2.5 rounded-sm bg-[#C1A661] hover:bg-[#E2CFA4] disabled:opacity-60 text-[#050B1E] text-[11px] tracking-[0.25em] uppercase font-semibold">
                  {lang === "fr" ? "Accorder l'abonnement" : "Grant subscription"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, textarea, type = "text", testid }) => (
  <div>
    <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C1A661] mb-1">{label}</label>
    {textarea ? (
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={4}
        className="w-full bg-[#080C17] border border-[#C1A661]/25 rounded-sm px-4 py-2 text-[#F8F5EE] text-sm focus:border-[#C1A661] outline-none" />
    ) : (
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#080C17] border border-[#C1A661]/25 rounded-sm px-4 py-2 text-[#F8F5EE] text-sm focus:border-[#C1A661] outline-none" />
    )}
  </div>
);

export default JournalPage;
